import { useQueries, useQuery } from '@tanstack/react-query'
import { mediaQueries } from '../../../../../api/media'
import { documentQueries } from '../../../../../api/documents'
import { eventQueries } from '../../../../../api/events'
import { boardRoomEventQueries } from '../../../../../api/boardroom-events'
import { pollQueries } from '../../../../../api/polls'
import { groupQueries } from '../../../../../api/groups'
import { getUserFullName, getUserInitials } from '../../../../../helpers/user'
import { IUserModel } from '../../../../../interfaces/models/users.interfaces'
import { ISocietiesModel } from '../../../../../interfaces/models/societies.interfaces'
import {
  PostAudience,
  PostDocument,
  PostEventCard,
  PostRow,
} from '../../../../../generated/api/post-row'
import { PostData } from './post-data'

const ids = (values: (string | undefined)[] | undefined): string[] =>
  (values || []).filter((value): value is string => !!value)

const audienceOf = (postData: PostData, groupNames: string[]): PostAudience => {
  if (postData.accessAdminMember) {
    return { scope: 'admins', groupNames }
  }
  if (postData.accessBoardMember) {
    return { scope: 'board', groupNames }
  }
  if (ids(postData.groupsIds).length > 0) {
    return { scope: 'groups', groupNames }
  }
  return { scope: 'everyone', groupNames: [] }
}

const NO_ACTIONS: PostRow['actions'] = {
  canLike: false,
  canComment: false,
  commentRoles: [],
  canEdit: false,
  canDelete: false,
  canPin: false,
  canReport: false,
}

export const usePreviewPostRow = (
  postData: PostData,
  society: ISocietiesModel,
  author: IUserModel | undefined
): PostRow => {
  const eventId = ids(postData.eventsIds)[0]
  const boardroomEventId = ids(postData.boardroomEventsIds)[0]
  const pollId = ids(postData.pollIds)[0]

  const { data: avatar } = useQuery(
    mediaQueries.getMedia(author?.avatarId ?? undefined)
  )
  const { data: cover } = useQuery(
    mediaQueries.getMedia(society.coverPhotoId ?? undefined)
  )
  const { data: event } = useQuery(eventQueries.getEvent(eventId))
  const { data: boardroomEvent } = useQuery(
    boardRoomEventQueries.getEvent(boardroomEventId)
  )
  const { data: poll } = useQuery(pollQueries.card(pollId))

  const media = useQueries({
    queries: ids(postData.media).map((id) => mediaQueries.getMedia(id)),
  }).flatMap((result) =>
    result.data?.url
      ? [
          {
            id: result.data._id,
            kind:
              result.data.cat === 'video'
                ? ('video' as const)
                : ('image' as const),
            url: result.data.url,
            thumbUrl: result.data.thumb,
          },
        ]
      : []
  )

  const documentIds = [
    ...ids(postData.documentsIds),
    ...ids(event?.documentsIds),
    ...ids(boardroomEvent?.documentsIds),
  ]
  const documentsById = new Map<string, PostDocument>(
    useQueries({
      queries: documentIds.map((id) => documentQueries.getDocument(id)),
    }).flatMap((result) =>
      result.data
        ? [
            [
              result.data._id,
              {
                id: result.data._id,
                name: result.data.name || result.data.filename,
                filename: result.data.filename,
                url: result.data.url || '',
              },
            ] as [string, PostDocument],
          ]
        : []
    )
  )
  const pickDocuments = (documents: string[]): PostDocument[] =>
    documents.flatMap((id) => {
      const document = documentsById.get(id)
      return document ? [document] : []
    })

  const groupNames = useQueries({
    queries: ids(postData.groupsIds).map((id) => groupQueries.getGroup(id)),
  }).flatMap((result) => (result.data ? [result.data.name] : []))

  const toEventCard = (
    kind: PostEventCard['kind'],
    source: typeof event | typeof boardroomEvent
  ): PostEventCard[] =>
    source
      ? [
          {
            id: source._id,
            societyId: source.societyId,
            kind,
            cancelled: false,
            title: source.title,
            description: source.description ?? '',
            location: source.location || undefined,
            startDate: new Date(source.startDate).getTime(),
            endDate: new Date(source.endDate).getTime(),
            isAllDay: source.isAllDay === true,
            documents: pickDocuments(ids(source.documentsIds)),
            attendance: { enabled: source.attendanceEnabled === true },
          },
        ]
      : []

  return {
    id: '',
    createdAt: Date.parse(postData.createdAt),
    updatedAt: postData.updatedAt ? Date.parse(postData.updatedAt) : 0,
    role: postData.role as PostRow['role'],
    message: postData.message || '',
    generatedMessage: false,
    isAlert: !!postData.isAlert,
    isPinned: false,
    isScheduled: false,
    author: author && {
      id: author._id,
      fullName: getUserFullName(author),
      initials: getUserInitials(author),
      avatarUrl: avatar?.url,
    },
    society: {
      id: society._id,
      name: society.name,
      type: society.societyTypeDisplayName,
      coverUrl: cover?.url,
    },
    audience: audienceOf(postData, groupNames),
    media,
    documents: pickDocuments(ids(postData.documentsIds)),
    resources: [],
    facilities: [],
    contacts: [],
    offers: [],
    events: [
      ...toEventCard('society', event),
      ...toEventCard('boardroom', boardroomEvent),
    ],
    polls: poll ? [poll] : [],
    likes: { count: 0, viewerLiked: false },
    comments: { count: 0, viewerCommented: false },
    reads: { count: 0, visible: false },
    actions: NO_ACTIONS,
  }
}
